import { useState } from 'react';

import type { CheckboxProps } from '../../Checkbox';
import type { FormControlProps } from '../../FormControl';
import type { SelectProps } from '../../Select';
import type { InputProps } from '../types';

type InputTypes = 'checkbox' | 'input' | 'select';

// https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
type ValidityKey = keyof Omit<ValidityState, 'customError' | 'valid'>;

type InputValidityKey<T extends InputTypes> = T extends 'input'
  ? ValidityKey
  : Extract<ValidityKey, 'valueMissing'>;

type GetPropFromInputValidityKey<T extends InputTypes> = Record<
  InputValidityKey<T>,
  keyof GetInputProps<T> | null
>;

const validityKeyToProp:
  | GetPropFromInputValidityKey<'checkbox'>
  | GetPropFromInputValidityKey<'input'>
  | GetPropFromInputValidityKey<'select'> = {
  badInput: null,
  patternMismatch: 'pattern',
  rangeOverflow: 'max',
  rangeUnderflow: 'min',
  stepMismatch: 'step',
  tooLong: 'maxLength',
  tooShort: 'minLength',
  typeMismatch: null,
  valueMissing: 'required',
};

type ValidationMessages = Record<ValidityKey, string>;

type ValidationConfig<K extends ValidityKey> = K extends
  | 'badInput'
  | 'typeMismatch'
  | 'valueMissing'
  ? { message: string }
  : K extends
        | 'rangeOverflow'
        | 'rangeUnderflow'
        | 'stepMismatch'
        | 'tooLong'
        | 'tooShort'
    ? { value: number; message: string }
    : { value: string; message: string };

export type Validation<T extends InputTypes> = {
  [K in InputValidityKey<T>]?: ValidationConfig<K>;
};

export interface CustomValidation {
  test: (value: string) => boolean;
  message: string;
}

type GetInputProps<T extends InputTypes> = T extends 'checkbox'
  ? CheckboxProps
  : T extends 'input'
    ? Omit<InputProps, 'children'>
    : Omit<SelectProps, 'children'>;

type GetInputElement<T extends InputTypes> = T extends 'checkbox' | 'input'
  ? HTMLInputElement
  : HTMLSelectElement;

type GetInputEvent<T extends InputTypes> = Parameters<
  NonNullable<GetInputProps<T>['onInvalid']>
>[0];

/**
 * Loops though all validationMessage keys and checks if the input has any item
 * in it's `validity` attribute that matches the validation message config.
 */
function getFailedValidity<T extends InputTypes>(
  inputElement: GetInputElement<T>,
  validationMessages: ValidationMessages
) {
  const validityToCheck = Object.keys(validationMessages) as Array<
    keyof ValidationMessages
  >;

  const failedKey = validityToCheck.find(
    validity => inputElement.validity[validity]
  );

  return failedKey ? validationMessages[failedKey] : undefined;
}

/**
 * Loops through all customValidations to find a failed test. Returns the custom
 * validation message.
 */
function getFailedCustom<T extends InputTypes>(
  inputElement: GetInputElement<T>,
  customValidations: CustomValidation[] = []
) {
  const failedCustom = customValidations.find(customValidation =>
    customValidation.test(String(inputElement.value))
  );

  const validationMessage = failedCustom?.message;

  inputElement.setCustomValidity(validationMessage ?? '');
  return validationMessage;
}

function getValidationError<T extends InputTypes>(
  inputElement: GetInputElement<T>,
  validationMessages: ValidationMessages,
  customValidations: CustomValidation[] | undefined
) {
  return (
    getFailedValidity(inputElement, validationMessages) ??
    getFailedCustom(inputElement, customValidations)
  );
}

export interface UseInputValidationProps<T extends InputTypes> {
  /* HTML validation object; the keys are different validation states and the objects define the constraint value and message to send the user */
  validation: Validation<T>;
  /* Custom validation functions that can test the input value and return true if it's invalid */
  customValidations?: CustomValidation[];
  /* Callback to notify the user when trying to submit a form with invalid inputs */
  onInvalidSubmit?: (
    inputElement: GetInputElement<T>,
    validationMessage: string
  ) => void;
}

/** Hook to assist with client side validation. */
export function useInputValidation<T extends InputTypes>({
  validation,
  customValidations,
  onInvalidSubmit,
}: UseInputValidationProps<T>) {
  const [validationError, setValidationError] = useState<string | undefined>();

  const validationKeys = Object.keys(validation) as Array<
    keyof typeof validation
  >;

  const inputValidityKeysToProp =
    validityKeyToProp as GetPropFromInputValidityKey<T>;

  const inputProps = {} as GetInputProps<T>;
  const validationMessages = {} as ValidationMessages;

  validationKeys.forEach(validityKey => {
    const inputProp = inputValidityKeysToProp[validityKey];

    const config = validation[validityKey];
    if (config) {
      if (inputProp && 'value' in config) {
        inputProps[inputProp] =
          config.value as GetInputProps<T>[keyof GetInputProps<T>];
      }

      if (inputProp === 'required') {
        inputProps['aria-required'] = true;
        inputProps.required = true;
      }

      validationMessages[validityKey] = config.message;
    }
  });

  const handleValidation = (
    eventOrElement: GetInputEvent<T> | GetInputElement<T>
  ) => {
    let inputElement: GetInputElement<T>;

    if ('target' in eventOrElement) {
      if (
        !(eventOrElement.target instanceof HTMLInputElement) &&
        !(eventOrElement.target instanceof HTMLSelectElement)
      ) {
        // if there's no target for the correct element, exit early since we can't do any validation
        return;
      }

      inputElement = eventOrElement.target as GetInputElement<T>;
    } else {
      inputElement = eventOrElement;
    }

    const newValidationError = getValidationError(
      inputElement,
      validationMessages,
      customValidations
    );

    setValidationError(newValidationError);

    return { inputElement, newValidationError };
  };

  // https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/invalid_event
  // this is only triggered if the form is submitted and the input is invalid
  inputProps.onInvalid = (event: GetInputEvent<T>) => {
    const { inputElement, newValidationError } = handleValidation(event) ?? {};

    if (onInvalidSubmit && inputElement) {
      onInvalidSubmit(
        inputElement,
        newValidationError ?? 'Unhandled validation detected'
      );
    }
  };

  inputProps.invalid = Boolean(validationError);

  const checkValidity = (
    event: GetInputEvent<T> | GetInputElement<T> | null
  ) => {
    if (!event) {
      return;
    }

    const { newValidationError } = handleValidation(event) ?? {};
    return !newValidationError;
  };

  const formControlProps: Pick<FormControlProps, 'invalid' | 'message'> = {
    invalid: Boolean(validationError),
  };

  if (validationError) {
    formControlProps.message = validationError;
  }

  return {
    checkValidity,
    validationError,
    inputProps,
    formControlProps,
  };
}
